import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@avila-roffe/components-web';
import {
  Braces,
  Brain,
  Check,
  ChevronDown,
  Database,
  Eye,
  Globe,
  ImageIcon,
  Mic,
  Monitor,
  Wrench,
  Zap,
} from 'lucide-react';
import { type ElementType, useState } from 'react';
import type { ModelRegistryEntry } from '../types';

// ─── Capabilities config ──────────────────────────────────────────────────────

export type CapabilityKey = keyof Pick<
  ModelRegistryEntry,
  | 'supportsTools'
  | 'supportsVision'
  | 'supportsReasoning'
  | 'supportsPromptCaching'
  | 'supportsJsonMode'
  | 'supportsAudioInput'
  | 'supportsWebSearch'
  | 'supportsComputerUse'
  | 'supportsStreaming'
  | 'supportsImageGeneration'
>;

export const MODEL_CAPABILITY_KEYS = [
  'supportsTools',
  'supportsVision',
  'supportsReasoning',
  'supportsPromptCaching',
  'supportsJsonMode',
  'supportsAudioInput',
  'supportsWebSearch',
  'supportsComputerUse',
  'supportsStreaming',
  'supportsImageGeneration',
] as const satisfies CapabilityKey[];

const MODEL_CAPABILITY_KEY_SET = new Set<string>(MODEL_CAPABILITY_KEYS);

export function isModelCapabilityKey(value: string): value is CapabilityKey {
  return MODEL_CAPABILITY_KEY_SET.has(value);
}

export function modelCapabilityKeys(values: readonly string[] | undefined): CapabilityKey[] {
  return (values ?? []).filter(isModelCapabilityKey);
}

export const CAPABILITIES: {
  key: CapabilityKey;
  label: string;
  description: string;
  icon: ElementType;
}[] = [
  {
    key: 'supportsTools',
    label: 'Tool Use',
    description: 'Can call external tools and functions during inference.',
    icon: Wrench,
  },
  {
    key: 'supportsVision',
    label: 'Vision',
    description: 'Accepts image inputs alongside text for multimodal understanding.',
    icon: Eye,
  },
  {
    key: 'supportsReasoning',
    label: 'Reasoning',
    description: 'Supports extended chain-of-thought reasoning (thinking / effort mode).',
    icon: Brain,
  },
  {
    key: 'supportsPromptCaching',
    label: 'Prompt Caching',
    description: 'Caches prompt prefixes server-side to reduce cost and latency on repeated calls.',
    icon: Database,
  },
  {
    key: 'supportsJsonMode',
    label: 'JSON Mode',
    description: 'Guarantees structured JSON output via response schema enforcement.',
    icon: Braces,
  },
  {
    key: 'supportsWebSearch',
    label: 'Web Search',
    description: 'Can query the web natively during inference without an external tool.',
    icon: Globe,
  },
  {
    key: 'supportsComputerUse',
    label: 'Computer Use',
    description: 'Can control a computer interface — mouse, keyboard, and screen — as an agent.',
    icon: Monitor,
  },
  {
    key: 'supportsAudioInput',
    label: 'Audio Input',
    description: 'Accepts audio inputs for transcription or spoken-language understanding.',
    icon: Mic,
  },
  {
    key: 'supportsStreaming',
    label: 'Streaming',
    description: 'Supports token-by-token streaming for real-time response rendering.',
    icon: Zap,
  },
  {
    key: 'supportsImageGeneration',
    label: 'Image Generation',
    description: 'Generates images from text prompts (e.g. DALL-E, GPT-Image).',
    icon: ImageIcon,
  },
];

// ─── CapabilityLegendPopover — "?" button listing all capabilities ────────────

export function CapabilityLegendPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Capabilities guide"
          className="flex size-4 items-center justify-center rounded-full border border-border text-[10px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          ?
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0 overflow-hidden" side="top" align="start">
        <div className="px-3 py-2 border-b border-border">
          <p className="text-xs font-semibold">Capability guide</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            What each icon means — and what it enables.
          </p>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-border/50">
          {CAPABILITIES.map(({ key, label, description, icon: Icon }) => (
            <div key={key} className="flex items-start gap-2.5 px-3 py-2">
              <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                <Icon className="size-3.5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-medium leading-snug">{label}</p>
                <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── CapabilityBadge — read-only icon with popover ────────────────────────────

export function CapabilityBadge({
  cap,
  supported,
}: {
  cap: (typeof CAPABILITIES)[number];
  supported: boolean;
}) {
  const Icon = cap.icon;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={cap.label}
          className={`flex size-8 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            supported
              ? 'bg-emerald-600 text-white dark:bg-emerald-700'
              : 'bg-muted text-muted-foreground/40'
          }`}
        >
          <Icon className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-3" side="top">
        <p className="text-xs font-semibold">{cap.label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{cap.description}</p>
        <p
          className={`mt-1.5 text-[11px] font-medium ${supported ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}
        >
          {supported ? 'Supported' : 'Not supported'}
        </p>
      </PopoverContent>
    </Popover>
  );
}

// ─── CapabilityToggle — compact clickable icon (New Release / inline forms) ───

export function CapabilityToggle({
  cap,
  selected,
  onToggle,
}: {
  cap: (typeof CAPABILITIES)[number];
  selected: boolean;
  onToggle: () => void;
}) {
  const Icon = cap.icon;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`${selected ? 'Remove' : 'Require'} ${cap.label}`}
            aria-pressed={selected}
            onClick={onToggle}
            className={`flex size-8 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              selected
                ? 'bg-emerald-600 text-white dark:bg-emerald-700'
                : 'bg-muted text-muted-foreground/40 hover:bg-muted/70 hover:text-muted-foreground'
            }`}
          >
            <Icon className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[180px] text-center">
          <p className="font-medium">{cap.label}</p>
          <p className="text-xs opacity-80">{cap.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── CapabilityRow — editable toggle row (ConfigTab) ─────────────────────────

export function CapabilityRow({
  cap,
  selected,
  onToggle,
  disabled,
}: {
  cap: (typeof CAPABILITIES)[number];
  selected: boolean;
  onToggle: () => void;
  disabled: boolean;
}) {
  const Icon = cap.icon;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      disabled={disabled}
      className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors border-b border-border/50 last:border-b-0 focus-visible:outline-none focus-visible:bg-accent/40 ${
        disabled ? 'cursor-default' : 'cursor-pointer hover:bg-accent/20'
      } ${selected ? 'bg-emerald-50/50 dark:bg-emerald-950/20' : ''}`}
    >
      <span
        className={`flex size-9 shrink-0 items-center justify-center rounded-xl transition-colors ${
          selected
            ? 'bg-emerald-600 text-white dark:bg-emerald-700'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold leading-snug">{cap.label}</span>
        <span className="block text-xs text-muted-foreground leading-snug mt-0.5">
          {cap.description}
        </span>
      </span>
      <span className="ml-2 shrink-0">
        {selected && <Check className="size-4 text-emerald-600 dark:text-emerald-400" />}
      </span>
    </button>
  );
}

// ─── ModelCapabilityBadges — mini inline badges for model rows ────────────────

export function ModelCapabilityBadges({ model }: { model: ModelRegistryEntry }) {
  return (
    <span className="flex items-center gap-0.5">
      {CAPABILITIES.map(({ key, label, icon: Icon }) => {
        const supported = model[key] === true;
        return (
          <span
            key={key}
            title={label}
            className={`flex size-4 items-center justify-center rounded ${
              supported ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/25'
            }`}
          >
            <Icon className="size-3" />
          </span>
        );
      })}
    </span>
  );
}

// ─── ModelSelectPopover — searchable model picker ─────────────────────────────

export function ModelSelectPopover({
  value,
  onChange,
  models,
  providers,
  disabled = false,
  allowNone = false,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  models: ModelRegistryEntry[];
  providers: string[];
  disabled?: boolean;
  allowNone?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = models.find((m) => m.modelName === value) ?? null;

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Select model"
          className={`flex h-9 w-full items-center justify-between rounded-[var(--field-radius,var(--radius))] border border-input bg-input-bg px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${
            !value ? 'text-muted-foreground' : ''
          }`}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 truncate">
            {selected ? (
              <>
                <span className="truncate">{selected.displayName ?? selected.modelName}</span>
                <ModelCapabilityBadges model={selected} />
              </>
            ) : allowNone ? (
              'Use agent default'
            ) : (
              'Select a model'
            )}
          </span>
          <ChevronDown className="ml-2 size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 flex flex-col overflow-hidden"
        style={{ maxHeight: 'min(20rem, var(--radix-popover-available-height, 20rem))' }}
        align="start"
        aria-label="Model selector"
      >
        <Command className="flex flex-col overflow-hidden bg-popover">
          <CommandInput placeholder="Search models…" />
          <CommandList
            className="overflow-y-auto overflow-x-hidden"
            style={{
              maxHeight: 'calc(min(var(--radix-popover-available-height, 20rem), 20rem) - 3rem)',
            }}
          >
            <CommandEmpty>No models found.</CommandEmpty>
            {allowNone && (
              <CommandItem
                value="__none__ use agent default"
                onSelect={() => {
                  onChange(undefined);
                  setOpen(false);
                }}
                className="flex items-center gap-2 text-muted-foreground"
              >
                {!value && <Check className="size-3.5 shrink-0 text-primary" />}
                <span>Use agent default</span>
              </CommandItem>
            )}
            {providers.map((provider, idx) => {
              const group = models.filter((m) => m.provider === provider);
              if (!group.length) return null;
              return (
                <span key={provider}>
                  {(allowNone || idx > 0) && <CommandSeparator />}
                  <CommandGroup heading={provider}>
                    {group.map((m) => (
                      <CommandItem
                        key={m.modelName}
                        value={`${provider} ${m.displayName ?? m.modelName} ${m.modelName}`}
                        onSelect={() => {
                          onChange(m.modelName);
                          setOpen(false);
                        }}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          {m.modelName === value && (
                            <Check className="size-3.5 shrink-0 text-primary" />
                          )}
                          <span className="truncate text-sm">{m.displayName ?? m.modelName}</span>
                        </span>
                        <ModelCapabilityBadges model={m} />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </span>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
